/**
 * Seeded star catalogue for the sky (spec §8: star points; style guide §1.1: the sky carries the saturation).
 * Pure and headless: directions, sizes in pixels and linear colours with the brightness folded in. Stars are
 * drawn live as pixel-sized billboards (sky/stars.ts), never baked, so they stay crisp at every preset
 * (a 256² face is 0.35° per texel, about 7 px at 1440p).
 */
import { createRng, nextFloat, type Rng } from '../../../core/rng'
import type { EnvironmentFile, Vec3Tuple } from '../data'

export type StarsConfig = EnvironmentFile['sky']['stars']

export interface StarField {
  readonly count: number
  /** Unit directions, xyz per star. */
  readonly dir: Float32Array
  /** Full width at half maximum in pixels, one per star. */
  readonly size: Float32Array
  /** Linear RGB times brightness (peak radiance), xyz per star. */
  readonly color: Float32Array
}

/**
 * Linear RGB (max component 1) of a black body at `kelvin`, after Tanner Helland's fit of the CIE data,
 * softened toward white because stars read as pastel to the eye.
 */
export function kelvinToLinearRgb(kelvin: number, out: [number, number, number]): [number, number, number] {
  const t = Math.min(40000, Math.max(1000, kelvin)) / 100
  let r: number
  let g: number
  let b: number
  if (t <= 66) {
    r = 255
    g = 99.4708025861 * Math.log(t) - 161.1195681661
    b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592)
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492)
    b = 255
  }
  const lin = (c: number): number => {
    const s = Math.min(255, Math.max(0, c)) / 255
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const soft = 0.3
  out[0] = lin(r) * (1 - soft) + soft
  out[1] = lin(g) * (1 - soft) + soft
  out[2] = lin(b) * (1 - soft) + soft
  const m = Math.max(out[0], out[1], out[2])
  out[0] /= m
  out[1] /= m
  out[2] /= m
  return out
}

/** Rec. 709 luminance. */
export function luminance(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function gaussian(rng: Rng): number {
  const u = Math.max(1e-9, nextFloat(rng))
  const v = nextFloat(rng)
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

/** A unit vector perpendicular to `n`. */
function perpendicular(n: Vec3Tuple): [number, number, number] {
  const a: Vec3Tuple = Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]
  const x = a[1] * n[2] - a[2] * n[1]
  const y = a[2] * n[0] - a[0] * n[2]
  const z = a[0] * n[1] - a[1] * n[0]
  const l = Math.hypot(x, y, z)
  return [x / l, y / l, z / l]
}

/**
 * The galactic band's plane normal: a seeded great circle that passes through the brightest nebula lobe
 * (`lobeDir`), so the band, the lobe and the rim light agree.
 */
export function bandNormalFor(seed: number, lobeDir: Vec3Tuple): [number, number, number] {
  const rng = createRng((seed ^ 0x5bd1e995) >>> 0)
  const a = perpendicular(lobeDir)
  const b: [number, number, number] = [
    lobeDir[1] * a[2] - lobeDir[2] * a[1],
    lobeDir[2] * a[0] - lobeDir[0] * a[2],
    lobeDir[0] * a[1] - lobeDir[1] * a[0],
  ]
  const phi = nextFloat(rng) * Math.PI * 2
  const c = Math.cos(phi)
  const s = Math.sin(phi)
  return [a[0] * c + b[0] * s, a[1] * c + b[1] * s, a[2] * c + b[2] * s]
}

/** Generates the star catalogue. Deterministic for a seed. */
export function generateStars(cfg: StarsConfig, seed: number, bandNormal: Vec3Tuple, bandWidthDeg: number): StarField {
  const rng = createRng((seed ^ 0x2545f491) >>> 0)
  const n = cfg.count
  const dir = new Float32Array(n * 3)
  const size = new Float32Array(n)
  const color = new Float32Array(n * 3)
  const u = perpendicular(bandNormal)
  const v: [number, number, number] = [
    bandNormal[1] * u[2] - bandNormal[2] * u[1],
    bandNormal[2] * u[0] - bandNormal[0] * u[2],
    bandNormal[0] * u[1] - bandNormal[1] * u[0],
  ]
  const sigma = ((bandWidthDeg * Math.PI) / 180) * 0.5
  const [bMin, bMax] = cfg.brightness
  const [sMin, sMax] = cfg.sizePx
  const [tMin, tMax] = cfg.temperatureK
  const logSpan = Math.log(bMax / bMin)
  const rgb: [number, number, number] = [0, 0, 0]
  for (let i = 0; i < n; i++) {
    let x: number
    let y: number
    let z: number
    if (nextFloat(rng) < cfg.bandShare) {
      const phi = nextFloat(rng) * Math.PI * 2
      const lat = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, gaussian(rng) * sigma))
      const c = Math.cos(lat)
      const s = Math.sin(lat)
      x = (u[0] * Math.cos(phi) + v[0] * Math.sin(phi)) * c + bandNormal[0] * s
      y = (u[1] * Math.cos(phi) + v[1] * Math.sin(phi)) * c + bandNormal[1] * s
      z = (u[2] * Math.cos(phi) + v[2] * Math.sin(phi)) * c + bandNormal[2] * s
    } else {
      z = nextFloat(rng) * 2 - 1
      const phi = nextFloat(rng) * Math.PI * 2
      const r = Math.sqrt(Math.max(0, 1 - z * z))
      x = r * Math.cos(phi)
      y = r * Math.sin(phi)
    }
    const l = Math.hypot(x, y, z)
    dir[i * 3] = x / l
    dir[i * 3 + 1] = y / l
    dir[i * 3 + 2] = z / l
    // Pareto brightness: many faint stars, few bright ones.
    const b = Math.min(bMax, bMin * Math.pow(1 - nextFloat(rng), -1 / cfg.powerLaw))
    const t = logSpan > 0 ? Math.log(b / bMin) / logSpan : 0
    size[i] = sMin + (sMax - sMin) * Math.pow(t, 0.7)
    // Brighter stars skew hotter (blue-white); the faint majority is K/G yellow-orange.
    const hot = Math.pow(nextFloat(rng), 1.8 - t * 1.2)
    kelvinToLinearRgb(tMin + (tMax - tMin) * hot, rgb)
    const k = b / luminance(rgb[0], rgb[1], rgb[2])
    color[i * 3] = rgb[0] * k
    color[i * 3 + 1] = rgb[1] * k
    color[i * 3 + 2] = rgb[2] * k
  }
  return { count: n, dir, size, color }
}
